import { create } from 'zustand'

import { MAX_STEPS_PER_TRAIL } from '../consts/agent-activity'
import { PENDING_TURN_MAX_AGE_MS, PENDING_TURN_POLL_INTERVAL_MS } from '../consts/pending-turn'
import {
  AgentActivityCloseReason,
  AgentActivityTrail,
  AgentStatusEventPayload,
} from '../core/types/agentStatus.types'
import { ChartConfig, ChatMode, FlowMetadata } from '../core/types/chat.types'
import { TurnWatchdog } from '../core/utils/turn-watchdog'
import { MaxChatMessage, MaxConversationSummary, MaxTurnState } from '../types'
import { cleanMessageId, findLatestFlowMetadata, normalizeFlowMetadata } from '../utils/flow'
import { maxMessageDebug } from '../utils/max-debug'
import { mergePendingTurns, PendingTurnMergeSource } from '../utils/turns/merge-pending-turns'
import {
  FrameScope,
  RealtimeFrameIdentity,
  RealtimeFrameScopeContext,
  resolveFrameScope as resolveTurnFrameScope,
} from '../utils/turns/resolve-frame-scope'
import useMaxFlowStore from './use-max-flow-store'
import useMaxSessionStore from './use-max-session-store'
import useMaxTurnStore from './use-max-turn-store'

const TURN_INACTIVITY_TIMEOUT_MS = 960_000
const TURN_ABSOLUTE_TIMEOUT_MS: number | null = null
const RESPONSE_TIMEOUT_ERROR = 'No response received. The message may not have been delivered.'

const migrationListeners = new Set<(conversationId: string) => void>()

export type FetchConversationMessages = (
  conversationId: string,
  tenantId: string
) => Promise<MaxChatMessage[]>

const notifyMigrationListeners = (conversationId: string) => {
  migrationListeners.forEach((listener) => {
    try {
      listener(conversationId)
    } catch (error) {
      maxMessageDebug.error('Chat store migration listener failed', {
        conversationId,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
    }
  })
}

type StreamingMessageParams = {
  requestId: string
  messageId: string
  conversationId: string
  createdAt?: number
  responseType?: string
  isFlow?: boolean
  flowMetadata?: FlowMetadata
  graphs?: ChartConfig[]
  /** Only `visible` writes reach the view; other scopes are kept in the turn store. */
  scope?: FrameScope
}

type MaxChatStoreState = {
  conversations: MaxConversationSummary[]
  pinnedConversations: MaxConversationSummary[]
  searchQuery: string
  messagesByConversation: Record<string, MaxChatMessage[]>
  activeConversationId: string | null
  tempConversationId: string | null
  awaitingConversationId: string | null
  activeMode: ChatMode
  pendingVisualizationByConversation: Record<string, number>
  isTyping: boolean
  transportError: string | null
  /** Transient reasoning trails keyed by backend requestId. Never persisted (D-07). */
  agentTrails: Record<string, AgentActivityTrail>
  /** Prodigy tenant the in-memory conversation state belongs to. */
  tenantScopeKey: string | null
  isLoadingMessages: boolean
}

type MaxChatStoreActions = {
  setConversations: (conversations: MaxConversationSummary[]) => void
  setConversationCollections: (params: {
    conversations: MaxConversationSummary[]
    pinnedConversations: MaxConversationSummary[]
  }) => void
  upsertConversation: (conversation: MaxConversationSummary) => void
  removeConversation: (conversationId: string) => void
  renameConversationLocal: (params: { conversationId: string; title: string }) => void
  pinConversationLocal: (conversationId: string) => void
  unpinConversationLocal: (conversationId: string) => void
  setSearchQuery: (query: string) => void
  clearSearch: () => void
  setMessages: (conversationId: string, messages: MaxChatMessage[]) => void
  appendMessage: (conversationId: string, message: MaxChatMessage) => void
  beginStreamingMessage: (params: StreamingMessageParams) => void
  updateStreamingContent: (params: StreamingMessageParams & { content: string }) => void
  completeStreamingMessage: (
    params: StreamingMessageParams & { content: string; tempConversationId?: string | null }
  ) => void
  setActiveConversationId: (conversationId: string | null) => void
  setTempConversationId: (tempConversationId: string | null) => void
  setAwaitingConversationId: (conversationId: string | null) => void
  setMode: (mode: ChatMode) => void
  syncModeWithFlow: (flowMetadata?: FlowMetadata | null) => void
  setTyping: (value: boolean) => void
  setTransportError: (error: string | null) => void
  migrateTempConversation: (params: {
    tempConversationId: string
    realConversationId: string
  }) => void
  onConversationMigrated: (handler: (conversationId: string) => void) => () => void
  updateConversationLocal: (params: {
    conversationId: string
    title?: string
    preview?: string
    updatedAt?: number
  }) => void
  setMessageFavoriteState: (
    conversationId: string,
    messageId: string,
    favorited: boolean
  ) => void
  markVisualizationPending: (conversationId?: string | null) => void
  consumeVisualizationPending: (conversationId?: string | null) => boolean
  resetConversationState: () => void
  setTenantScopeKey: (tenantScopeKey: string | null) => void
  /**
   * Shows a conversation: the cached list at once (merged with its turns in flight), then the
   * server list merged the same way. Starts polling when the last message is an unanswered user
   * message and no turn of it is in memory (after a reload or a tenant switch, V2-D05).
   */
  loadMessages: (conversationId: string, fetchMessages: FetchConversationMessages) => Promise<void>
  /** Invalidates in-flight history requests and polling, e.g. when a new chat starts. */
  resetHistoryRequests: () => void
  stopPendingTurnPolling: () => void
  recalculateTypingForCurrentConversation: () => void
  /** Drops a new conversation that finished while it was not on screen. */
  discardTempConversation: (tempConversationId: string) => void
  resolveFrameScope: (frame: RealtimeFrameIdentity) => FrameScope
  /** Writes the turn's current text to the view when, and only when, it is visible. */
  syncTurnToView: (clientRequestId: string | null | undefined) => void
  ingestAgentStatus: (payload: AgentStatusEventPayload) => void
  bindAgentTrailMessage: (params: {
    requestId: string
    messageId: string
    conversationId?: string | null
  }) => void
  closeAgentTrail: (requestId: string, reason: AgentActivityCloseReason) => void
  closeAgentTrailsForConversation: (
    conversationId: string | null | undefined,
    reason: AgentActivityCloseReason
  ) => void
  clearAgentTrails: () => void
  startTurnWatchdog: (scopeId: string) => void
  noteTurnActivity: (conversationId?: string | null) => void
  stopTurnWatchdog: (scopeId: string) => void
  stopAllTurnWatchdogs: () => void
}

type MaxChatStore = MaxChatStoreState & MaxChatStoreActions

const sortConversations = (conversations: MaxConversationSummary[]): MaxConversationSummary[] =>
  [...conversations].sort((a, b) => b.updatedAt - a.updatedAt)

const dedupeConversations = (conversations: MaxConversationSummary[]): MaxConversationSummary[] => {
  const unique = new Map<string, MaxConversationSummary>()
  conversations.forEach((conversation) => {
    unique.set(conversation.id, conversation)
  })
  return Array.from(unique.values())
}

const normalizeConversations = (
  conversations: MaxConversationSummary[],
  options?: { pinned?: boolean }
): MaxConversationSummary[] =>
  sortConversations(
    dedupeConversations(
      conversations.map((conversation) => ({
        ...conversation,
        isPinned: options?.pinned ?? conversation.isPinned ?? false,
      }))
    )
  )

const mergeMessages = (currentMessage: MaxChatMessage, nextMessage: MaxChatMessage): MaxChatMessage => ({
  ...currentMessage,
  ...nextMessage,
  id: nextMessage.id || currentMessage.id,
  messageId: nextMessage.messageId || currentMessage.messageId || nextMessage.id,
  requestId: nextMessage.requestId || currentMessage.requestId,
  createdAt: currentMessage.createdAt || nextMessage.createdAt,
})

const dedupeMessages = (messages: MaxChatMessage[]): MaxChatMessage[] => {
  const uniqueOrder: MaxChatMessage[] = []
  const indexByKey = new Map<string, number>()

  messages.forEach((message) => {
    const key = message.messageId || message.id
    const existingIndex = indexByKey.get(key)

    if (existingIndex === undefined) {
      indexByKey.set(key, uniqueOrder.length)
      uniqueOrder.push(message)
      return
    }

    uniqueOrder[existingIndex] = mergeMessages(uniqueOrder[existingIndex]!, message)
  })

  return uniqueOrder
}

const findMessageIndex = (
  messages: MaxChatMessage[],
  params: {
    id?: string
    messageId?: string
    requestId?: string
  }
): number => {
  return messages.findIndex((message) => {
    if (params.requestId && message.requestId === params.requestId) {
      return true
    }

    const candidateMessageId = cleanMessageId(params.messageId || params.id)
    if (
      candidateMessageId &&
      (cleanMessageId(message.messageId) === candidateMessageId ||
        cleanMessageId(message.id) === candidateMessageId)
    ) {
      return true
    }

    return false
  })
}

const logMessageOrder = (
  label: string,
  conversationId: string,
  messages: MaxChatMessage[],
  extra?: Record<string, unknown>
) => {
  maxMessageDebug.state(label, {
    conversationId,
    totalMessages: messages.length,
    lastMessageId: messages[messages.length - 1]?.id || null,
    lastMessageRole: messages[messages.length - 1]?.role || null,
    messages,
    ...extra,
  })
}

const updateConversationCollection = (
  collection: MaxConversationSummary[],
  params: {
    conversationId: string
    title?: string
    preview?: string
    updatedAt?: number
  }
): MaxConversationSummary[] =>
  collection.map((conversation) =>
    conversation.id === params.conversationId
      ? {
          ...conversation,
          title: params.title ?? conversation.title,
          preview: params.preview ?? conversation.preview,
          updatedAt: params.updatedAt ?? conversation.updatedAt,
        }
      : conversation
  )

const shouldUpdateMessageFavoriteState = (message: MaxChatMessage, targetMessageId: string): boolean => {
  const candidateIds = [message.messageId, message.id]
  return candidateIds.some((candidateId) => {
    if (!candidateId) return false
    return candidateId === targetMessageId || cleanMessageId(candidateId) === cleanMessageId(targetMessageId)
  })
}

/** The real conversation on screen; null for a new chat that only has a temporary id. */
const getVisibleConversationId = (
  state: Pick<MaxChatStoreState, 'activeConversationId' | 'tempConversationId'>
): string | null =>
  state.activeConversationId && state.activeConversationId !== state.tempConversationId
    ? state.activeConversationId
    : null

const getFrameScopeContext = (
  state: Pick<MaxChatStoreState, 'activeConversationId' | 'tempConversationId'>
): RealtimeFrameScopeContext => ({
  selectedTenantId: useMaxSessionStore.getState().session.maxTenantId,
  visibleConversationId: getVisibleConversationId(state),
  activeTempConversationId: state.tempConversationId,
})

const isAgentTrailVisible = (
  trail: AgentActivityTrail,
  state: Pick<MaxChatStoreState, 'activeConversationId' | 'tempConversationId'>,
  turns: Record<string, MaxTurnState>
): boolean => {
  if (trail.clientRequestId) {
    return (
      resolveTurnFrameScope(
        { clientRequestId: trail.clientRequestId },
        turns[trail.clientRequestId],
        getFrameScopeContext(state)
      ) === 'visible'
    )
  }

  // Direct store consumers predating the envelope can still inspect a trail;
  // realtime handlers never ingest an unscoped frame.
  const visibleConversationId = getVisibleConversationId(state)
  return (
    trail.conversationId === visibleConversationId ||
    (visibleConversationId === null && state.tempConversationId !== null)
  )
}

/** The trail occupying the "assistant is working" slot of the conversation on screen. */
export const selectActiveAgentTrail = (
  state: Pick<MaxChatStoreState, 'agentTrails' | 'activeConversationId' | 'tempConversationId'>,
  turns: Record<string, MaxTurnState>
): AgentActivityTrail | null => {
  let activeTrail: AgentActivityTrail | null = null

  Object.values(state.agentTrails).forEach((trail) => {
    if (
      trail.status === 'active' &&
      trail.steps.length > 0 &&
      isAgentTrailVisible(trail, state, turns)
    ) {
      activeTrail = trail
    }
  })

  return activeTrail
}

/** The closed trail anchored above an assistant message, when it belongs to the view. */
export const selectAgentTrailByMessageId = (
  state: Pick<MaxChatStoreState, 'agentTrails' | 'activeConversationId' | 'tempConversationId'>,
  turns: Record<string, MaxTurnState>,
  messageId: string | null | undefined
): AgentActivityTrail | null => {
  if (!messageId) return null

  return (
    Object.values(state.agentTrails).find(
      (trail) =>
        trail.status === 'closed' &&
        trail.messageId === messageId &&
        isAgentTrailVisible(trail, state, turns)
    ) || null
  )
}

const watchdog = new TurnWatchdog({
  inactivityMs: TURN_INACTIVITY_TIMEOUT_MS,
  absoluteMs: TURN_ABSOLUTE_TIMEOUT_MS,
  onExpire: (scopeId) => {
    const store = useMaxChatStore.getState()
    store.closeAgentTrailsForConversation(scopeId, 'timeout')

    const visibleConversationId = getVisibleConversationId(store)
    const isActiveScope =
      visibleConversationId === scopeId ||
      (visibleConversationId === null && store.tempConversationId === scopeId)

    if (!isActiveScope) {
      maxMessageDebug.error('Chat store response timeout for inactive conversation', {
        scopeId,
      })
      return
    }

    maxMessageDebug.error('Chat store response timeout fired', {
      scopeId,
      timeoutMs: TURN_INACTIVITY_TIMEOUT_MS,
    })
    useMaxChatStore.setState({
      isTyping: false,
      awaitingConversationId: null,
      transportError: RESPONSE_TIMEOUT_ERROR,
    })
  },
})

const resolveWatchdogScope = (conversationId?: string | null): string | null => {
  if (conversationId && watchdog.isRunning(conversationId)) return conversationId
  const tempId = useMaxChatStore.getState().tempConversationId
  if (tempId && watchdog.isRunning(tempId)) return tempId
  return conversationId ?? null
}

const getSelectedTenantId = (): string => useMaxSessionStore.getState().session.maxTenantId ?? ''

const buildAssistantMessageFromTurn = (turn: MaxTurnState): MaxChatMessage | null => {
  if (!turn.assistantMessageId || !turn.conversationId) return null

  const final = turn.final
  const content = final?.content ?? turn.visibleContent
  if (!content.trim()) return null

  const streamMetadata = turn.streamMetadata
  return {
    id: turn.assistantMessageId,
    messageId: turn.assistantMessageId,
    requestId: turn.requestId || undefined,
    conversationId: turn.conversationId,
    role: 'assistant',
    content,
    createdAt: final?.createdAt ?? turn.startedAt,
    isFlow: final?.isFlow ?? streamMetadata.isFlow,
    flowMetadata:
      normalizeFlowMetadata(final?.flowMetadata ?? streamMetadata.flowMetadata) || undefined,
    responseType: final?.responseType ?? streamMetadata.responseType,
    graphs: final?.graphs,
  }
}

let loadMessagesSequence = 0
let messagesRequestGeneration = 0
const historyRequestGenerations = new Map<string, number>()
let pendingTurnFetchMessages: FetchConversationMessages | null = null
let pendingTurnPollTimer: ReturnType<typeof setTimeout> | null = null
let pendingTurnPollKey: string | null = null
let pendingTurnPollTenantId: string | null = null
let pendingTurnPollConversationId: string | null = null
let pendingTurnPollUserCreatedAt: number | null = null
let pendingTurnPollLastServerMessages: MaxChatMessage[] | null = null
let pendingTurnPollInFlight = false
let pendingTurnPollGeneration = 0

const getPendingTurnPollKey = (tenantId: string, conversationId: string): string =>
  `${tenantId}\u0000${conversationId}`

const nextHistoryRequestGeneration = (tenantId: string, conversationId: string): number => {
  const key = getPendingTurnPollKey(tenantId, conversationId)
  const generation = (historyRequestGenerations.get(key) ?? 0) + 1
  historyRequestGenerations.set(key, generation)
  return generation
}

const isCurrentHistoryRequest = (
  tenantId: string,
  conversationId: string,
  generation: number
): boolean =>
  historyRequestGenerations.get(getPendingTurnPollKey(tenantId, conversationId)) === generation &&
  getSelectedTenantId() === tenantId

const isPendingTurnWithoutMemory = (
  serverMessages: MaxChatMessage[],
  tenantId: string,
  conversationId: string
): boolean => {
  if (useMaxTurnStore.getState().forConversation(tenantId, conversationId).length > 0) return false

  const lastMessage = serverMessages[serverMessages.length - 1]
  if (!lastMessage || lastMessage.role !== 'user') return false

  const createdAt = new Date(lastMessage.createdAt).getTime()
  return Number.isFinite(createdAt) && Date.now() - createdAt < PENDING_TURN_MAX_AGE_MS
}

const isCurrentPendingTurnPoll = (generation: number, requestGeneration: number): boolean => {
  const tenantId = getSelectedTenantId()
  return (
    generation === pendingTurnPollGeneration &&
    requestGeneration === messagesRequestGeneration &&
    pendingTurnPollTenantId === tenantId &&
    pendingTurnPollConversationId === getVisibleConversationId(useMaxChatStore.getState()) &&
    pendingTurnPollKey ===
      (pendingTurnPollConversationId
        ? getPendingTurnPollKey(tenantId, pendingTurnPollConversationId)
        : null)
  )
}

const applyMessagesForConversation = (
  conversationId: string,
  list: MaxChatMessage[],
  source: PendingTurnMergeSource
): MaxChatMessage[] => {
  const turnStore = useMaxTurnStore.getState()
  const result = mergePendingTurns(
    list,
    turnStore.forConversation(getSelectedTenantId(), conversationId),
    {
      source,
      buildAssistantMessage: buildAssistantMessageFromTurn,
    }
  )

  if (source === 'server') {
    result.confirmedTurnIds.forEach((clientRequestId) => turnStore.remove(clientRequestId))
  }

  const store = useMaxChatStore.getState()
  store.setMessages(conversationId, result.messages)
  const latestFlow = findLatestFlowMetadata(result.messages)
  useMaxFlowStore.getState().setFlowMetadata(conversationId, latestFlow)
  store.syncModeWithFlow(latestFlow)
  return result.messages
}

const schedulePendingTurnPolling = (
  serverMessages: MaxChatMessage[],
  tenantId: string,
  conversationId: string
): void => {
  const store = useMaxChatStore.getState()
  if (!isPendingTurnWithoutMemory(serverMessages, tenantId, conversationId)) {
    if (pendingTurnPollKey === getPendingTurnPollKey(tenantId, conversationId)) {
      store.stopPendingTurnPolling()
    }
    store.recalculateTypingForCurrentConversation()
    return
  }

  const key = getPendingTurnPollKey(tenantId, conversationId)
  if (pendingTurnPollKey && pendingTurnPollKey !== key) {
    store.stopPendingTurnPolling()
  }

  pendingTurnPollKey = key
  pendingTurnPollTenantId = tenantId
  pendingTurnPollConversationId = conversationId
  pendingTurnPollLastServerMessages = [...serverMessages]
  pendingTurnPollUserCreatedAt = new Date(
    serverMessages[serverMessages.length - 1]!.createdAt
  ).getTime()

  if (pendingTurnPollTimer === null && !pendingTurnPollInFlight) {
    const generation = pendingTurnPollGeneration
    pendingTurnPollTimer = setTimeout(() => {
      pendingTurnPollTimer = null
      void pollPendingTurn(generation)
    }, PENDING_TURN_POLL_INTERVAL_MS)
  }

  store.recalculateTypingForCurrentConversation()
}

const pollPendingTurn = async (generation: number): Promise<void> => {
  if (!isCurrentPendingTurnPoll(generation, messagesRequestGeneration)) return

  const store = useMaxChatStore.getState()
  const tenantId = pendingTurnPollTenantId
  const conversationId = pendingTurnPollConversationId
  const userCreatedAt = pendingTurnPollUserCreatedAt
  const fetchMessages = pendingTurnFetchMessages
  if (!tenantId || !conversationId || userCreatedAt === null || !fetchMessages) {
    store.stopPendingTurnPolling()
    store.recalculateTypingForCurrentConversation()
    return
  }

  // Always ends: when the answer shows up or at the age cap, without an error (V2-D05).
  if (Date.now() - userCreatedAt >= PENDING_TURN_MAX_AGE_MS) {
    store.stopPendingTurnPolling()
    store.recalculateTypingForCurrentConversation()
    return
  }

  messagesRequestGeneration += 1
  const requestGeneration = messagesRequestGeneration
  const historyRequestGeneration = nextHistoryRequestGeneration(tenantId, conversationId)
  pendingTurnPollInFlight = true
  try {
    const loadedMessages = await fetchMessages(conversationId, tenantId)
    if (!isCurrentPendingTurnPoll(generation, requestGeneration)) return
    if (!isCurrentHistoryRequest(tenantId, conversationId, historyRequestGeneration)) return

    applyMessagesForConversation(conversationId, loadedMessages, 'server')
    schedulePendingTurnPolling(loadedMessages, tenantId, conversationId)
  } catch (error) {
    if (isCurrentPendingTurnPoll(generation, requestGeneration)) {
      maxMessageDebug.error('Chat store pending turn poll failed', {
        conversationId,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
    }
  } finally {
    if (isCurrentPendingTurnPoll(generation, requestGeneration)) {
      pendingTurnPollInFlight = false
      const latestMessages = pendingTurnPollLastServerMessages ?? []
      schedulePendingTurnPolling(latestMessages, tenantId, conversationId)
    }
  }
}

const evaluatePendingTurnPolling = (
  serverMessages: MaxChatMessage[],
  tenantId: string,
  conversationId: string
): void => {
  if (
    getSelectedTenantId() !== tenantId ||
    getVisibleConversationId(useMaxChatStore.getState()) !== conversationId
  ) {
    return
  }

  schedulePendingTurnPolling(serverMessages, tenantId, conversationId)
}

const useMaxChatStore = create<MaxChatStore>((set, get) => ({
  conversations: [],
  pinnedConversations: [],
  searchQuery: '',
  messagesByConversation: {},
  activeConversationId: null,
  tempConversationId: null,
  awaitingConversationId: null,
  activeMode: 'DEFAULT',
  pendingVisualizationByConversation: {},
  isTyping: false,
  transportError: null,
  agentTrails: {},
  tenantScopeKey: null,
  isLoadingMessages: false,

  setConversations: (conversations) =>
    set({
      conversations: normalizeConversations(
        conversations.filter((conversation) => !conversation.isPinned),
        { pinned: false }
      ),
      pinnedConversations: normalizeConversations(
        conversations.filter((conversation) => conversation.isPinned),
        { pinned: true }
      ),
    }),

  setConversationCollections: ({ conversations, pinnedConversations }) =>
    set({
      conversations: normalizeConversations(conversations, { pinned: false }),
      pinnedConversations: normalizeConversations(pinnedConversations, { pinned: true }),
    }),

  upsertConversation: (conversation) =>
    set((state) => {
      const normalizedConversation = {
        ...conversation,
        isPinned: conversation.isPinned ?? false,
      }

      if (normalizedConversation.isPinned) {
        const nextPinned = state.pinnedConversations.filter((item) => item.id !== conversation.id)
        return {
          pinnedConversations: normalizeConversations([...nextPinned, normalizedConversation], {
            pinned: true,
          }),
          conversations: state.conversations.filter((item) => item.id !== conversation.id),
        }
      }

      const existing = state.conversations.filter((item) => item.id !== normalizedConversation.id)
      return {
        conversations: normalizeConversations([...existing, normalizedConversation], {
          pinned: false,
        }),
        pinnedConversations: state.pinnedConversations.filter(
          (item) => item.id !== normalizedConversation.id
        ),
      }
    }),

  removeConversation: (conversationId) =>
    set((state) => ({
      conversations: state.conversations.filter((conversation) => conversation.id !== conversationId),
      pinnedConversations: state.pinnedConversations.filter(
        (conversation) => conversation.id !== conversationId
      ),
      messagesByConversation: Object.fromEntries(
        Object.entries(state.messagesByConversation).filter(([id]) => id !== conversationId)
      ),
      activeConversationId:
        state.activeConversationId === conversationId ? null : state.activeConversationId,
      awaitingConversationId:
        state.awaitingConversationId === conversationId ? null : state.awaitingConversationId,
      tempConversationId: state.tempConversationId === conversationId ? null : state.tempConversationId,
    })),

  renameConversationLocal: ({ conversationId, title }) =>
    set((state) => ({
      conversations: state.conversations.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, title, updatedAt: Date.now() }
          : conversation
      ),
      pinnedConversations: state.pinnedConversations.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, title, updatedAt: Date.now() }
          : conversation
      ),
    })),

  pinConversationLocal: (conversationId) =>
    set((state) => {
      const target = state.conversations.find((conversation) => conversation.id === conversationId)
      if (!target) return state

      return {
        conversations: state.conversations.filter((conversation) => conversation.id !== conversationId),
        pinnedConversations: normalizeConversations(
          [{ ...target, isPinned: true, updatedAt: Date.now() }, ...state.pinnedConversations],
          { pinned: true }
        ),
      }
    }),

  unpinConversationLocal: (conversationId) =>
    set((state) => {
      const target = state.pinnedConversations.find((conversation) => conversation.id === conversationId)
      if (!target) return state

      return {
        pinnedConversations: state.pinnedConversations.filter(
          (conversation) => conversation.id !== conversationId
        ),
        conversations: normalizeConversations(
          [{ ...target, isPinned: false, updatedAt: Date.now() }, ...state.conversations],
          { pinned: false }
        ),
      }
    }),

  setSearchQuery: (searchQuery) => set({ searchQuery: searchQuery.trim() }),

  clearSearch: () => set({ searchQuery: '' }),

  setMessages: (conversationId, messages) =>
    set((state) => {
      const nextMessages = dedupeMessages(messages)
      logMessageOrder('Chat store setMessages committed', conversationId, nextMessages, {
        reason: 'history_replace',
      })

      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: nextMessages,
        },
      }
    }),

  appendMessage: (conversationId, message) =>
    set((state) => {
      const current = state.messagesByConversation[conversationId] || []
      const nextMessages = dedupeMessages([...current, message])

      logMessageOrder('Chat store appendMessage committed', conversationId, nextMessages, {
        appendedMessageId: message.id,
        appendedRole: message.role,
        appendedCreatedAt: message.createdAt,
      })

      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: nextMessages,
        },
      }
    }),

  beginStreamingMessage: ({
    requestId,
    messageId,
    conversationId,
    createdAt,
    responseType,
    isFlow,
    flowMetadata,
    graphs,
    scope = 'visible',
  }) => {
    if (scope !== 'visible') return

    set((state) => {
      const current = state.messagesByConversation[conversationId] || []
      const existingIndex = findMessageIndex(current, { messageId })
      if (existingIndex !== -1) {
        return state
      }

      const nextMessages = [
        ...current,
        {
          id: messageId,
          messageId,
          requestId,
          conversationId,
          role: 'assistant' as const,
          content: '',
          createdAt: createdAt ?? Date.now(),
          responseType,
          isFlow,
          flowMetadata,
          graphs,
        },
      ]

      logMessageOrder('Chat store beginStreamingMessage committed', conversationId, nextMessages, {
        requestId,
        messageId,
        responseType: responseType || null,
      })

      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: nextMessages,
        },
      }
    })
  },

  updateStreamingContent: (params) => {
    const scope = params.scope ?? 'visible'
    if (scope !== 'visible') return

    const current = get().messagesByConversation[params.conversationId] || []
    if (findMessageIndex(current, { messageId: params.messageId }) === -1) {
      // D-29: a streaming write never creates an empty bubble; creation waits for real text.
      if (!params.content.trim()) return
      get().beginStreamingMessage({ ...params, scope })
    }

    set((state) => {
      const messages = state.messagesByConversation[params.conversationId] || []
      const index = findMessageIndex(messages, { messageId: params.messageId })
      if (index === -1) return state

      const existing = messages[index]!
      const nextMessages = [...messages]
      nextMessages[index] = {
        ...existing,
        content: params.content,
        createdAt: existing.createdAt || params.createdAt || Date.now(),
        requestId: existing.requestId || params.requestId || undefined,
        conversationId: params.conversationId,
        isFlow: params.isFlow ?? existing.isFlow,
        flowMetadata: params.flowMetadata ?? existing.flowMetadata,
        responseType: params.responseType ?? existing.responseType,
        graphs: params.graphs ?? existing.graphs,
      }

      logMessageOrder(
        'Chat store updateStreamingContent committed',
        params.conversationId,
        nextMessages,
        {
          requestId: params.requestId,
          messageId: params.messageId,
          contentLength: params.content.length,
          responseType: params.responseType || null,
        }
      )

      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [params.conversationId]: nextMessages,
        },
      }
    })
  },

  completeStreamingMessage: (params) => {
    const scope = params.scope ?? 'visible'
    if (scope === 'unknown' || scope === 'foreign') return

    watchdog.stop(params.conversationId)
    if (params.tempConversationId) watchdog.stop(params.tempConversationId)

    if (scope === 'background') return

    // The final payload is authoritative and may legitimately be empty (flow-only answers),
    // so it creates its message explicitly instead of going through the D-29 guard (A-20).
    get().beginStreamingMessage({ ...params, scope })
    get().updateStreamingContent({ ...params, scope })

    if (params.flowMetadata) {
      get().syncModeWithFlow(params.flowMetadata)
    }

    get().setTyping(false)
  },

  setActiveConversationId: (activeConversationId) => set({ activeConversationId }),

  setTempConversationId: (tempConversationId) => set({ tempConversationId }),

  setAwaitingConversationId: (awaitingConversationId) => set({ awaitingConversationId }),

  setMode: (activeMode) => set({ activeMode }),

  syncModeWithFlow: (flowMetadata) => {
    const flow = flowMetadata?.flow
    if (typeof flow !== 'string') return

    const upperFlow = flow.toUpperCase()
    if (upperFlow === 'EXPLORE') {
      set({ activeMode: 'EXPLORE' })
    } else if (upperFlow === 'BUDGET') {
      set({ activeMode: 'BUDGET' })
    }
  },

  setTyping: (isTyping) => {
    maxMessageDebug.event('Chat store setTyping', {
      isTyping,
      activeConversationId: useMaxChatStore.getState().activeConversationId,
      awaitingConversationId: useMaxChatStore.getState().awaitingConversationId,
    })
    set({ isTyping })
  },

  setTransportError: (transportError) => {
    maxMessageDebug.event('Chat store setTransportError', {
      transportError,
      activeConversationId: useMaxChatStore.getState().activeConversationId,
      awaitingConversationId: useMaxChatStore.getState().awaitingConversationId,
    })
    set({ transportError })
  },

  migrateTempConversation: ({ tempConversationId, realConversationId }) => {
    let migrated = false

    // Keep the watchdog alive under the real conversation ID after migration.
    if (
      useMaxChatStore.getState().tempConversationId === tempConversationId &&
      watchdog.isRunning(tempConversationId)
    ) {
      watchdog.stop(tempConversationId)
      watchdog.start(realConversationId)
    }

    set((state) => {
      const tempMessages = state.messagesByConversation[tempConversationId] || []
      const realMessages = state.messagesByConversation[realConversationId] || []
      if (state.tempConversationId !== tempConversationId) {
        return state
      }
      const migratedMessages = [...tempMessages, ...realMessages].map((message) => ({
        ...message,
        conversationId: realConversationId,
      }))

      const nextMessagesByConversation = { ...state.messagesByConversation }
      delete nextMessagesByConversation[tempConversationId]
      nextMessagesByConversation[realConversationId] = dedupeMessages(migratedMessages)
      logMessageOrder(
        'Chat store migrateTempConversation committed',
        realConversationId,
        nextMessagesByConversation[realConversationId]!,
        {
          tempConversationId,
          realConversationId,
        }
      )

      const migratedUpdate = (conversation: MaxConversationSummary): MaxConversationSummary =>
        conversation.id === realConversationId
          ? { ...conversation, isTemporary: false, updatedAt: Date.now() }
          : conversation

      const nextConversations = state.conversations
        .filter((conversation) => conversation.id !== tempConversationId)
        .map(migratedUpdate)

      const nextPinnedConversations = state.pinnedConversations
        .filter((conversation) => conversation.id !== tempConversationId)
        .map(migratedUpdate)

      migrated = true

      return {
        messagesByConversation: nextMessagesByConversation,
        conversations: normalizeConversations(nextConversations, { pinned: false }),
        pinnedConversations: normalizeConversations(nextPinnedConversations, { pinned: true }),
        tempConversationId: null,
        awaitingConversationId:
          state.awaitingConversationId === tempConversationId
            ? realConversationId
            : state.awaitingConversationId,
        activeConversationId: realConversationId,
      }
    })

    if (migrated) {
      notifyMigrationListeners(realConversationId)
    }
  },

  onConversationMigrated: (handler) => {
    migrationListeners.add(handler)
    return () => {
      migrationListeners.delete(handler)
    }
  },

  updateConversationLocal: ({ conversationId, title, preview, updatedAt }) =>
    set((state) => ({
      conversations: updateConversationCollection(state.conversations, {
        conversationId,
        title,
        preview,
        updatedAt,
      }),
      pinnedConversations: updateConversationCollection(state.pinnedConversations, {
        conversationId,
        title,
        preview,
        updatedAt,
      }),
    })),

  setMessageFavoriteState: (conversationId, messageId, favorited) =>
    set((state) => {
      const current = state.messagesByConversation[conversationId] || []
      if (!current.length) {
        return state
      }

      let didChange = false
      const nextMessages = current.map((message) => {
        if (!shouldUpdateMessageFavoriteState(message, messageId)) {
          return message
        }

        if (Boolean(message.fav) === favorited) {
          return message
        }

        didChange = true
        return {
          ...message,
          fav: favorited,
        }
      })

      if (!didChange) {
        return state
      }

      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: nextMessages,
        },
      }
    }),

  markVisualizationPending: (conversationId) => {
    if (!conversationId) return

    set((state) => ({
      pendingVisualizationByConversation: {
        ...state.pendingVisualizationByConversation,
        [conversationId]: (state.pendingVisualizationByConversation[conversationId] || 0) + 1,
      },
    }))
  },

  consumeVisualizationPending: (conversationId) => {
    if (!conversationId) return false

    const pendingCount = useMaxChatStore.getState().pendingVisualizationByConversation[conversationId] || 0
    if (pendingCount <= 0) {
      return false
    }

    set((state) => {
      const next = { ...state.pendingVisualizationByConversation }
      if (pendingCount === 1) {
        delete next[conversationId]
      } else {
        next[conversationId] = pendingCount - 1
      }

      return {
        pendingVisualizationByConversation: next,
      }
    })

    return true
  },

  resetConversationState: () => {
    set({
      conversations: [],
      pinnedConversations: [],
      searchQuery: '',
      messagesByConversation: {},
      activeConversationId: null,
      tempConversationId: null,
      awaitingConversationId: null,
      activeMode: 'DEFAULT',
      pendingVisualizationByConversation: {},
      isTyping: false,
      transportError: null,
    })
  },

  setTenantScopeKey: (tenantScopeKey) => set({ tenantScopeKey }),

  loadMessages: async (conversationId, fetchMessages) => {
    loadMessagesSequence += 1
    messagesRequestGeneration += 1
    const requestSequence = loadMessagesSequence
    const requestGeneration = messagesRequestGeneration
    const requestTenantId = getSelectedTenantId()
    const historyRequestGeneration = nextHistoryRequestGeneration(requestTenantId, conversationId)
    pendingTurnFetchMessages = fetchMessages

    const isCurrentRequest = () =>
      requestSequence === loadMessagesSequence &&
      requestGeneration === messagesRequestGeneration &&
      getSelectedTenantId() === requestTenantId &&
      getVisibleConversationId(get()) === conversationId

    try {
      if (pendingTurnPollKey !== null) {
        get().stopPendingTurnPolling()
      }

      if (get().activeConversationId !== conversationId) {
        set({ activeConversationId: conversationId })
      }

      const cachedMessages = get().messagesByConversation[conversationId]
      const hasCache = Boolean(cachedMessages && cachedMessages.length > 0)

      if (!cachedMessages || !hasCache) {
        // No cache - show loading and fetch
        set({ isLoadingMessages: true })

        const loadedMessages = await fetchMessages(conversationId, requestTenantId)
        if (!isCurrentHistoryRequest(requestTenantId, conversationId, historyRequestGeneration)) {
          return
        }
        if (!isCurrentRequest()) {
          // The user moved on: only refresh the cached list of that conversation.
          get().setMessages(conversationId, loadedMessages)
          return
        }

        applyMessagesForConversation(conversationId, loadedMessages, 'server')
        evaluatePendingTurnPolling(loadedMessages, requestTenantId, conversationId)
        return
      }

      // Has cache - show immediately, no loading
      applyMessagesForConversation(conversationId, cachedMessages, 'cache')
      evaluatePendingTurnPolling(cachedMessages, requestTenantId, conversationId)

      // Fetch in background
      fetchMessages(conversationId, requestTenantId)
        .then((loadedMessages) => {
          if (!isCurrentHistoryRequest(requestTenantId, conversationId, historyRequestGeneration)) {
            return
          }
          if (!isCurrentRequest()) {
            get().setMessages(conversationId, loadedMessages)
            return
          }

          applyMessagesForConversation(conversationId, loadedMessages, 'server')
          evaluatePendingTurnPolling(loadedMessages, requestTenantId, conversationId)
          get().recalculateTypingForCurrentConversation()
        })
        .catch((error) => {
          maxMessageDebug.error('Chat store background history fetch failed', {
            conversationId,
            errorMessage: error instanceof Error ? error.message : String(error),
          })
        })
    } catch (error) {
      if (!isCurrentRequest()) return
      get().setTransportError(error instanceof Error ? error.message : String(error))
    } finally {
      if (isCurrentRequest()) {
        set({ isLoadingMessages: false })
        get().recalculateTypingForCurrentConversation()
      }
    }
  },

  resetHistoryRequests: () => {
    loadMessagesSequence += 1
    messagesRequestGeneration += 1
    historyRequestGenerations.clear()
    get().stopPendingTurnPolling()
    set({ isLoadingMessages: false })
  },

  stopPendingTurnPolling: () => {
    if (pendingTurnPollTimer !== null) {
      clearTimeout(pendingTurnPollTimer)
    }
    pendingTurnPollTimer = null
    pendingTurnPollKey = null
    pendingTurnPollTenantId = null
    pendingTurnPollConversationId = null
    pendingTurnPollUserCreatedAt = null
    pendingTurnPollLastServerMessages = null
    pendingTurnPollInFlight = false
    pendingTurnPollGeneration += 1
  },

  recalculateTypingForCurrentConversation: () => {
    const conversationId = getVisibleConversationId(get())
    const tenantId = getSelectedTenantId()
    if (!conversationId) {
      get().setTyping(false)
      return
    }

    // Typing while a turn of this conversation has neither its final answer nor visible text,
    // or while a pending turn without memory is being polled.
    const hasMemoryPendingTurn = useMaxTurnStore
      .getState()
      .forConversation(tenantId, conversationId)
      .some((turn) => !turn.final && turn.visibleContent.length === 0)
    const hasPendingPoll = pendingTurnPollKey === getPendingTurnPollKey(tenantId, conversationId)
    get().setTyping(hasMemoryPendingTurn || hasPendingPoll)
  },

  discardTempConversation: (tempConversationId) =>
    set((state) => {
      const nextMessagesByConversation = { ...state.messagesByConversation }
      delete nextMessagesByConversation[tempConversationId]

      return {
        messagesByConversation: nextMessagesByConversation,
        conversations: state.conversations.filter(
          (conversation) => conversation.id !== tempConversationId
        ),
        pinnedConversations: state.pinnedConversations.filter(
          (conversation) => conversation.id !== tempConversationId
        ),
        tempConversationId:
          state.tempConversationId === tempConversationId ? null : state.tempConversationId,
        awaitingConversationId:
          state.awaitingConversationId === tempConversationId ? null : state.awaitingConversationId,
      }
    }),

  resolveFrameScope: (frame) => {
    const state = get()
    return resolveTurnFrameScope(
      frame,
      useMaxTurnStore.getState().get(frame.clientRequestId),
      getFrameScopeContext(state)
    )
  },

  syncTurnToView: (clientRequestId) => {
    const turn = useMaxTurnStore.getState().get(clientRequestId)
    if (!turn) return

    const state = get()
    const scope = resolveTurnFrameScope(
      { clientRequestId: turn.clientRequestId },
      turn,
      getFrameScopeContext(state)
    )
    if (scope !== 'visible' || !turn.assistantMessageId || !turn.conversationId) return

    // A new chat is shown from its temporary bucket until the final response migrates it.
    const viewConversationId = getVisibleConversationId(state) ?? state.tempConversationId
    if (!viewConversationId) return

    const finalContent = turn.final
    const metadata = finalContent
      ? {
          isFlow: finalContent.isFlow,
          flowMetadata: finalContent.flowMetadata,
          responseType: finalContent.responseType,
          graphs: finalContent.graphs,
        }
      : {
          isFlow: turn.streamMetadata.isFlow,
          flowMetadata: turn.streamMetadata.flowMetadata,
          responseType: turn.streamMetadata.responseType,
        }

    get().updateStreamingContent({
      requestId: turn.requestId || '',
      messageId: turn.assistantMessageId,
      conversationId: viewConversationId,
      content: finalContent?.content ?? turn.visibleContent,
      createdAt: finalContent?.createdAt,
      ...metadata,
      scope: 'visible',
    })
  },

  ingestAgentStatus: (payload) =>
    set((state) => {
      const existing = state.agentTrails[payload.requestId]
      const trail: AgentActivityTrail = existing
        ? { ...existing, steps: [...existing.steps] }
        : {
            requestId: payload.requestId,
            clientRequestId: payload.clientRequestId,
            conversationId: payload.conversationId,
            messageId: null,
            steps: [],
            status: 'active',
            droppedSteps: 0,
            startedAt: new Date(),
            closedAt: null,
            closeReason: null,
          }

      const lastStep = trail.steps[trail.steps.length - 1]
      // Consecutive duplicates (same phase + label) collapse into one step (A-03).
      if (lastStep && lastStep.phase === payload.phase && lastStep.label === payload.label) {
        return state
      }

      const sequence = trail.steps.length
      trail.steps.push({
        id: `${payload.requestId}:${sequence}`,
        label: payload.label,
        phase: payload.phase,
        agent: payload.agent,
        timestamp: payload.timestamp,
        receivedAt: new Date(),
        sequence,
      })

      while (trail.steps.length > MAX_STEPS_PER_TRAIL) {
        trail.steps.shift()
        trail.droppedSteps += 1
      }

      return {
        agentTrails: {
          ...state.agentTrails,
          [trail.requestId]: trail,
        },
      }
    }),

  bindAgentTrailMessage: ({ requestId, messageId, conversationId }) =>
    set((state) => {
      const trail = state.agentTrails[requestId]
      if (!trail) return state

      return {
        agentTrails: {
          ...state.agentTrails,
          [requestId]: {
            ...trail,
            messageId,
            conversationId: conversationId || trail.conversationId,
          },
        },
      }
    }),

  closeAgentTrail: (requestId, reason) =>
    set((state) => {
      const trail = state.agentTrails[requestId]
      if (!trail || trail.status === 'closed') return state

      return {
        agentTrails: {
          ...state.agentTrails,
          [requestId]: {
            ...trail,
            status: 'closed',
            closedAt: new Date(),
            closeReason: reason,
          },
        },
      }
    }),

  closeAgentTrailsForConversation: (conversationId, reason) => {
    if (!conversationId) return

    Object.values(get().agentTrails).forEach((trail) => {
      if (trail.status === 'active' && trail.conversationId === conversationId) {
        get().closeAgentTrail(trail.requestId, reason)
      }
    })
  },

  clearAgentTrails: () => set({ agentTrails: {} }),

  startTurnWatchdog: (scopeId) => watchdog.start(scopeId),

  noteTurnActivity: (conversationId) => {
    const scopeId = resolveWatchdogScope(conversationId)
    if (scopeId) watchdog.notify(scopeId)
  },

  stopTurnWatchdog: (scopeId) => watchdog.stop(scopeId),

  stopAllTurnWatchdogs: () => watchdog.stopAll(),
}))

export default useMaxChatStore
